/** Transfer a multipart body with separate upload-stall and response deadlines. */

export interface MultipartUploadTimeouts {
  stallMs: number;
  responseMs: number;
}

export function uploadMultipart(
  url: string,
  options: RequestInit,
  timeouts: MultipartUploadTimeouts,
): Promise<Response> {
  if (!(options.body instanceof FormData)) {
    throw new TypeError("Multipart upload requires FormData");
  }
  const body = options.body;
  if (timeouts.stallMs <= 0 || timeouts.responseMs <= 0) {
    throw new RangeError("Multipart upload timeouts must be positive");
  }

  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const signal = options.signal;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    let lastLoaded = 0;

    const clearDeadline = () => {
      if (deadline !== undefined) clearTimeout(deadline);
      deadline = undefined;
    };
    const finish = (outcome: { response: Response } | { error: unknown }) => {
      if (settled) return;
      settled = true;
      clearDeadline();
      signal?.removeEventListener("abort", abortFromCaller);
      if ("response" in outcome) resolve(outcome.response);
      else reject(outcome.error);
    };
    const abortForTimeout = (message: string) => {
      finish({ error: new DOMException(message, "TimeoutError") });
      xhr.abort();
    };
    const armStallDeadline = () => {
      clearDeadline();
      deadline = setTimeout(
        () =>
          abortForTimeout(
            `Upload stalled: no bytes sent for ${timeouts.stallMs / 1000}s.`,
          ),
        timeouts.stallMs,
      );
    };
    const abortFromCaller = () => {
      finish({
        error:
          signal?.reason ?? new DOMException("Upload cancelled", "AbortError"),
      });
      xhr.abort();
    };

    xhr.upload.onprogress = (event) => {
      if (event.loaded > lastLoaded) {
        lastLoaded = event.loaded;
        armStallDeadline();
      }
    };
    xhr.upload.onload = () => {
      if (settled) return;
      clearDeadline();
      deadline = setTimeout(
        () =>
          abortForTimeout(
            `Upload completed, but the server did not respond within ${timeouts.responseMs / 1000}s.`,
          ),
        timeouts.responseMs,
      );
    };
    xhr.onload = () => {
      if (xhr.status === 0) {
        finish({ error: new TypeError("Failed to fetch") });
        return;
      }
      const headers = new Headers();
      xhr
        .getAllResponseHeaders()
        .trim()
        .split(/\r?\n/)
        .filter(Boolean)
        .forEach((line) => {
          const colon = line.indexOf(":");
          if (colon > 0)
            headers.append(line.slice(0, colon), line.slice(colon + 1).trim());
        });
      finish({
        response: new Response(
          xhr.status === 204 || xhr.status === 205 ? null : xhr.responseText,
          { status: xhr.status, statusText: xhr.statusText, headers },
        ),
      });
    };
    xhr.onerror = () => finish({ error: new TypeError("Failed to fetch") });
    xhr.onabort = () =>
      finish({ error: new DOMException("Upload cancelled", "AbortError") });

    try {
      xhr.open(options.method ?? "POST", url);
      xhr.withCredentials = true;
      new Headers(options.headers).forEach((value, name) =>
        xhr.setRequestHeader(name, value),
      );

      if (signal?.aborted) {
        abortFromCaller();
        return;
      }
      signal?.addEventListener("abort", abortFromCaller);
      armStallDeadline();
      xhr.send(body);
    } catch (error) {
      finish({ error });
    }
  });
}
